import React from "react";
import { useTranslation } from "react-i18next";
import getSymbolFromCurrency from "currency-symbol-map";
import moment from "moment";

import { orderCurrencySymbol } from "../../../../../services/orderService";
import { getRegionalConfig } from "../../../../../config";
import { formatMoney } from "../../../../../util/numberUtils";
import IconMC from "../../../../components/IconMC";
import SelectedPaymentMethods from "./SelectedPaymentMethods";

const OrderHowMessage = ({ order, paymentMethods }) => {
  const { t } = useTranslation('Inbox');

  if (!order) return null;

  const { products = [], delivery_method } = order || {};
  const currency = order?.currency || getRegionalConfig().currency;
  const currency_symbol = order
    ? orderCurrencySymbol(order)
    : getSymbolFromCurrency(currency) || '$';
  const selectedPaymentMethods = paymentMethods.filter((p) => p.selected);
  const formattedDateTime = moment().local().locale('es').format('DD-MM-YYYY HH:mm');

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      <span className="tw-font-medium tw-text-gray-950">{t('sendPaymentLinks.subtitle3')}</span>

      <div className="tw-flex tw-flex-col tw-p-3 tw-rounded-xl tw-bg-[#D9FDD3] tw-gap-2 tw-text-xs">
        <span className="tw-font-bold tw-gray-950">{t('sendPaymentLinks.orderDetailTitle')}</span>
        {products.length > 0 && (
          <div className="tw-flex tw-flex-col">
            <span className="tw-font-bold tw-gray-950">{t('sendPaymentLinks.products')}</span>
            {products.map((item, index) => (
              <div key={`product-${index}`} className="tw-flex tw-flex-col">
                <span>
                  {item.title}
                  {' '}
                  X
                  {' '}
                  {item.quantity}
                </span>
              </div>
            ))}
          </div>
        )}

        {delivery_method && (
          <div className="tw-flex tw-flex-col">
            <span className="tw-font-bold tw-gray-950">{t('sendPaymentLinks.delivery')}</span>

            <div className="tw-flex tw-flex-col">
              {delivery_method === "store_pickup" && (
                <span>{t('sendPaymentLinks.pickUpInStore')}</span>
              )}
              {delivery_method === "send_order" && (
                <>
                  <span>{t('sendPaymentLinks.sendOrder')}</span>
                  <span className="tw-flex tw-text-xs">
                    {t('sendPaymentLinks.address')}
                    {': '}
                    {order.address || order.customer?.address}
                    ,&nbsp;
                    {order.city || order.customer?.city}
                    ,&nbsp;
                    {order.state || order.customer?.state}
                    ,&nbsp;
                    {order.country || order.customer?.country_id}
                    ,&nbsp;
                    {order.zip_code || order.customer?.zip_code}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        <div className="tw-flex tw-flex-col">
          <span className="tw-font-bold tw-gray-950">{t('sendPaymentLinks.summary')}</span>
          <div className="tw-flex tw-font-regular tw-gray-950 tw-gap-1">
            <span>
              {t('sendPaymentLinks.subtotal')}
              :
            </span>
            <span>
              {currency_symbol}
              {formatMoney(order.subtotal || 0)}
              {' '}
              {currency}
            </span>
          </div>

          {Number(order.total_discount || 0) > 0 && (
            <>
              <div className="tw-flex tw-gray-950 tw-gap-1">
                <span>
                  {t('sendPaymentLinks.discount')}
                  :
                </span>
                <span>
                  -
                  {currency_symbol}
                  {formatMoney(order.total_discount)}
                  {' '}
                  {currency}
                </span>
              </div>

              <div className="tw-flex tw-gray-950 tw-gap-1">
                <span>
                  {t('sendPaymentLinks.subtotalWithDiscount')}
                  :
                </span>
                <span>
                  {currency_symbol}
                  {formatMoney(order.subtotal_with_discount || 0)}
                  {' '}
                  {currency}
                </span>
              </div>
            </>
          )}

          <div className="tw-flex tw-gray-950 tw-gap-1">
            <span>
              {t('sendPaymentLinks.tax')}
              :
            </span>
            <span className="tw-font-regular tw-gray-950">
              {currency_symbol}
              {formatMoney(order.tax || 0)}
              {' '}
              {currency}
            </span>
          </div>

          <div className="tw-flex tw-gray-950 tw-gap-1">
            <span>
              {t('sendPaymentLinks.shippingCost')}
              :
            </span>
            <span className="tw-font-regular tw-gray-950">
              {currency_symbol}
              {formatMoney(order.shipping_value || 0)}
              {' '}
              {currency}
            </span>
          </div>
        </div>

        <div className="tw-flex tw-gray-950 tw-gap-1">
          <span className="tw-font-bold">
            {t('sendPaymentLinks.total')}
            :
          </span>
          <span>
            {currency_symbol}
            {formatMoney(order.total || 0)}
            {' '}
            {currency}
          </span>
        </div>

        {selectedPaymentMethods.length > 0 && (
          <div className="tw-flex tw-flex-col">
            <span className="tw-font-bold tw-gray-950">{t('sendPaymentLinks.availablePaymentMethods')}</span>
            <SelectedPaymentMethods
              selectedPaymentMethods={selectedPaymentMethods}
              keyPrefix="list"
              linkClassName="tw-text-2xs tw-text-blue-500 tw-underline tw-decoration-blue-500"
            />
          </div>
        )}

        <div className="tw-flex tw-justify-end tw-items-center">
          <span className="tw-text-3xs tw-text-gray-500">{formattedDateTime}</span>
          <IconMC
            name="double-check"
            className="tw-text-blue-500"
            size="base"
          />
        </div>
      </div>
    </div>
  );
};

export default OrderHowMessage;
